import React, { useRef, useState } from "react";
import { makeRequest } from "../makeRequest";

interface UploadResponse {
  success: boolean;
  url: string;
  filename: string;
}

interface ImageUploaderProps {
  onUploadComplete?: (uploadedUrl: string) => void; // optional callback
  resize?: boolean; // optional resize
}

const MAX_SIZE = 3 * 1024 * 1024;

export const ImageUploader: React.FC<ImageUploaderProps> = ({ onUploadComplete, resize = false }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [uploadedUrl, setUploadedUrl] = useState<string>("");

  const validateFile = (f: File | undefined) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      alert("Only image files allowed");
      return;
    }
    if (f.size > MAX_SIZE) {
      alert("Max 3MB allowed");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateFile(e.target.files?.[0]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    validateFile(e.dataTransfer.files?.[0]);
  };

  const uploadFile = async () => {
    if (!file) return alert("Select an image");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "gallery/images");
      formData.append("resize", resize ? "1" : "0");

      setProgress(0);

      const data = await makeRequest<UploadResponse>("/admin/adminuploadimage", {
        method: "POST",
        body: formData,
        auth: true,
      });

      setUploadedUrl(data.url);
      setProgress(100);

      if (onUploadComplete) onUploadComplete(data.url);
    } catch (err: any) {
      alert(err.message || "Upload failed");
      setProgress(0);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto mt-8">
      <div
        className="border-2 border-dashed border-gray-400 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="image/*"
          ref={inputRef}
          className="hidden"
          onChange={handleSelect}
        />
        {!preview ? (
          <p className="text-gray-500">Click or Drag & Drop an image</p>
        ) : (
          <img
            src={preview}
            alt="preview"
            className="max-h-40 mx-auto rounded shadow"
          />
        )}
      </div>

      <button
        onClick={uploadFile}
        disabled={!file}
        className="w-full bg-blue-600 text-white mt-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
      >
        Upload
      </button>

      {progress > 0 && progress < 100 && (
        <div className="w-full bg-gray-200 h-2 rounded mt-3">
          <div
            className="bg-blue-600 h-2 rounded"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {uploadedUrl && (
        <div className="mt-4 bg-green-100 p-3 rounded text-sm break-all flex flex-wrap gap-2 justify-center items-center">
          <img className="w-10 h-10 rounded-lg" src={uploadedUrl} alt="" />
        </div>
      )}
    </div>
  );
};